// Generic storage store factory with Pinia + LiveQuery
import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { ServicesBuilder } from './setup';
import { TStorageService, StorageService, Storage } from '@posx/core';
import { Subscription } from 'dexie';
import { logLiveQueryError } from '@/utils/logger';

export interface StorageItem { uid: string }
export interface RemarkItem extends StorageItem { name: string; usage_count: number }

export const defineStorageStore = <T extends StorageItem>(model: string) => defineStore(`storage-${model}`, {
  state: () => ({ data: [] as T[], _svc: null as TStorageService<T[]> | null, _sub: null as Subscription | null }),
  actions: {
    // service singleton is method-only, skip deep proxy
    async _svc_() { return this._svc ??= markRaw(await ServicesBuilder.getInstance().getService<TStorageService<T[]>>(StorageService<T[]>)); },
    async _save() {
      const s = await this._svc_(), r = await s.table.where('model').equals(model).filter(x => x.deleted_at == null).first() ?? Object.assign(new Storage<T[]>(), { model });
      r.data = this.data as T[];
      r.id_in_server ? await s.updateOne(r.id_in_server, r) : await s.addOne(r);
    },
    async getAll() { const s = await this._svc_(), r = await s.table.where('model').equals(model).filter(x => x.deleted_at == null).first(); this.$patch({ data: r?.data ?? [] }); return this.data as T[]; },
    async addOne(item: T) { (this.data as T[]).push(item); await this._save(); },
    async updateOne(item: T) { const i = this.data.findIndex(x => x.uid === item.uid); if (i >= 0) { (this.data as T[])[i] = item; await this._save(); } },
    async deleteOne(item: T) { const i = this.data.findIndex(x => x.uid === item.uid); if (i >= 0) { this.data.splice(i, 1); await this._save(); } },
    async addAll(items: T[]) { this.data = items as typeof this.data; await this._save(); },
    // For RemarkItem types: prepend genuinely new names to front, ignore existing ones (order stays user-controlled) unless recent moves used ones to front
    async saveNames(names: string[], recent = false) {
      const data = this.data as unknown as RemarkItem[], existing = new Set(data.map(r => r.name));
      const fresh: RemarkItem[] = [...new Set(names)].filter(n => !existing.has(n)).map(name => ({ uid: name, name, usage_count: 0 }));
      if (!fresh.length && !recent) return;
      // Stable sort moves used names to front, keeping relative order within each group
      this.data = [...fresh, ...(recent ? [...data].sort((a, b) => +names.includes(b.name) - +names.includes(a.name)) : data)] as unknown as typeof this.data;
      await this._save();
    },
    async startLiveQuery() {
      if (this._sub) return;
      const s = await this._svc_();
      // Subscription is method-only, skip deep proxy
      this._sub = markRaw(s.liveQuery(() => s.table.where('model').equals(model).filter(x => x.deleted_at == null).first()).subscribe({
        next: r => { this.$patch({ data: r?.data ?? [] }); },
        error: err => logLiveQueryError(err, { model }),
      }));
    },
    stopLiveQuery() { this._sub?.unsubscribe(); this._sub = null; },
  },
});
